import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {Link} from '@github-ui/react-router'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {RepositoryMilestonesInternal} from '@github-ui/repository-milestone/RepositoryMilestones'
import {Button} from '@primer/react'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {renderIssuesSidebar} from '../components/sidebar/render-issues-sidebar'
import {useSetCurrentViewId} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {IssueIndexPage, RepositoryMilestoneNewPage, RepositoryMilestonePage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {RepositoryMilestoneIndexPageContentInternal$key} from './__generated__/RepositoryMilestoneIndexPageContentInternal.graphql'
import type {RepositoryMilestoneIndexPageQuery} from './__generated__/RepositoryMilestoneIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneIndexPageQuery(
    $name: String!
    $owner: String!
    $state: MilestoneState!
    $orderField: MilestoneOrderField = CREATED_AT
    $orderDirection: OrderDirection = DESC
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestoneIndexPageContentInternal
        @arguments(state: $state, orderField: $orderField, orderDirection: $orderDirection)
    }
  }
`

export const RepositoryMilestoneIndexPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneIndexPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Index">
      <RepositoryMilestoneIndexContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneIndexContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneIndexPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneIndexPageQuery>(PageQuery, pageQueryRef)
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')

  useEffect(() => {
    return preloadWhenIdle(() => {
      RepositoryMilestonePage.preload()
      RepositoryMilestoneNewPage.preload()
      // When issue_pinned_views is on, the sidebar hook preloads IssueIndexPage.
      // Remove this line when the flag is cleaned up.
      if (!pinnedViewsEnabled) {
        IssueIndexPage.preload()
      }
    })
    // pinnedViewsEnabled is read once at mount; flag flips during a session are
    // not expected to retroactively change the preload set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setCurrentViewId = useSetCurrentViewId()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneIndexPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneIndexPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneIndexPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneIndexPageContentInternal on Repository
      @argumentDefinitions(
        state: {type: "MilestoneState!"}
        orderField: {type: "MilestoneOrderField", defaultValue: CREATED_AT}
        orderDirection: {type: "OrderDirection", defaultValue: DESC}
      ) {
        viewerCanPush
        nameWithOwner
        ...RepositoryMilestonesInternal
          @arguments(state: $state, orderField: $orderField, orderDirection: $orderDirection)
      }
    `,
    repository,
  )

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const headerActions =
    sidebarLayoutEnabled && data.viewerCanPush ? (
      <Button
        as={Link}
        to={`/${data.nameWithOwner}/milestones/new`}
        variant="primary"
        data-testid="new-milestone-button"
      >
        New milestone
      </Button>
    ) : undefined

  return (
    <RepoIssuesPageLayout
      dialogTitle="Milestones"
      title={sidebarLayoutEnabled ? 'Milestones' : undefined}
      actions={headerActions}
      renderSidebar={renderIssuesSidebar}
    >
      <RepositoryMilestonesInternal repository={data} />
    </RepoIssuesPageLayout>
  )
}
